import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { menuSections } from "@/data/menuData";

const Item = ({ item, L, mp }) => (
  <div className="mi" data-testid="menu-item">
    <div className="mi__head">
      <h4 className="mi__name">{L(item.name)}</h4>
      <span className="mi__dots" />
      <span className="mi__price">
        {item.price}
        {item.per === "pc" && <em className="mi__per"> / pc</em>}
      </span>
    </div>
    {item.desc && <p className="mi__desc">{L(item.desc)}</p>}
    {item.tag && <span className="mi__tag">{L(item.tag)}</span>}
  </div>
);

export default function MenuPage() {
  const { t, L } = useLang();
  const mp = t.menuPage;
  const [active, setActive] = useState("starters");

  const scrollTo = (id) => {
    setActive(id);
    document.getElementById(`ms-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="menu-page" data-testid="menu-page">
      <header className="menu-page__hero">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="section-label">{mp.label}</p>
          <h1 className="menu-page__title">
            {mp.titleA}
            <br />
            <em>{mp.titleB}</em>
          </h1>
          <p className="menu-page__intro">{mp.intro}</p>
        </motion.div>
      </header>

      <nav className="menu-page__nav" data-testid="menu-section-nav">
        {menuSections.map((s) => (
          <button
            key={s.id}
            className={`menu-page__navbtn ${active === s.id ? "is-active" : ""}`}
            onClick={() => scrollTo(s.id)}
            data-testid={`menu-nav-${s.id}`}
          >
            {s.gr}
          </button>
        ))}
      </nav>

      <div className="menu-page__body">
        {menuSections.map((s) => (
          <section
            key={s.id}
            id={`ms-${s.id}`}
            className={`ms ${s.night ? "ms--night" : ""}`}
            data-testid={`menu-section-${s.id}`}
          >
            <div className="ms__header">
              <span className="ms__gr">{s.gr}</span>
              <div>
                <h2 className="ms__title">{L(s.title)}</h2>
                <p className="ms__time">{typeof s.time === "string" ? s.time : L(s.time)}</p>
              </div>
            </div>
            <p className="ms__note">{L(s.note)}</p>

            {s.featured && (
              <div className="ms__featured" data-testid="menu-featured">
                <span className="ms__featured-tag">{L(s.featured.tag)}</span>
                <div className="ms__featured-row">
                  <h3>{L(s.featured.name)}</h3>
                  <span className="ms__featured-price">
                    {s.featured.price} <em>{mp.perPerson}</em>
                  </span>
                </div>
                <p>{L(s.featured.desc)}</p>
              </div>
            )}

            {s.kilo && (
              <div className="ms__kilo" data-testid="menu-kilo">
                <h3 className="ms__kilo-title">{L(s.kilo.title)}</h3>
                <p className="ms__kilo-desc">{L(s.kilo.desc)}</p>
                <div className="ms__kilo-grid">
                  {s.kilo.items.map((k) => (
                    <div key={k.name.en} className="ms__kilo-item">
                      <span>{L(k.name)}</span>
                      <span className="ms__kilo-price">
                        {k.price} <em>{mp.perKg}</em>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {s.items.length > 0 && (
              <div className="ms__items">
                {s.items.map((item) => (
                  <Item key={item.name.en} item={item} L={L} mp={mp} />
                ))}
              </div>
            )}

            {s.groups &&
              s.groups.map((g) => (
                <div key={g.title.en} className="ms__group">
                  <h3 className="ms__group-title">{L(g.title)}</h3>
                  <div className="ms__items">
                    {g.items.map((item) => (
                      <Item key={item.name.en} item={item} L={L} mp={mp} />
                    ))}
                  </div>
                </div>
              ))}
          </section>
        ))}
      </div>

      <div className="menu-page__cta">
        <p className="menu-page__cta-quote">
          {t.footer.quoteA} {t.footer.quoteB}
        </p>
        <a href="tel:+306936614442" className="btn btn--gold" data-testid="menupage-reserve-cta">
          {mp.reserve}
        </a>
      </div>
    </main>
  );
}
